<template>
  <div v-if="showReferAFriend" class="refer-a-friend">
    <v-banner single-line color="primary" class="refer-a-friend-banner">
      <a class="open-modal-link" @click="dialog = true">
        Refer to friends for {{ referralAmount }} instant cash per friend
      </a>
      <template #actions="{ dismiss }">
        <v-icon class="modal-close" color="black" @click.stop="dismiss">{{
          icons.mdiClose
        }}</v-icon>
      </template>
    </v-banner>

    <v-dialog v-model="dialog" content-class="crud-dialog" max-width="600px">
      <v-card class="crud-card">
        <v-card-title class="crud-card-title">
          <v-row no-gutters>
            <v-col cols="11" class="text-left">
              <h2 class="text-h5">Refer your friends</h2>
            </v-col>
            <v-col cols="1">
              <v-icon
                class="modal-close"
                color="black"
                @click.stop="closeDialog()"
                >{{ icons.mdiClose }}</v-icon
              >
            </v-col>
          </v-row>
        </v-card-title>
        <v-divider />
        <v-card-text class="crud-card-text">
          <div v-if="loading" class="text-center">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <div v-else>
            <ValidationObserver ref="observer">
              <v-form class="modal-form" @submit.prevent="submit">
                <div
                  v-for="(nameAndEmail, index) in nameAndEmails"
                  :key="index"
                >
                  <v-row no-gutters>
                    <v-col cols="5" class="pr-3">
                      <ValidationProvider
                        :id="'name' + index"
                        v-slot="{ errors }"
                        :vid="'name' + index"
                        name="name"
                        rules="required"
                      >
                        <v-text-field
                          v-model="nameAndEmail.name"
                          label="Name*"
                          :error-messages="errors"
                        />
                      </ValidationProvider>
                    </v-col>

                    <v-col cols="5" class="pl-3">
                      <ValidationProvider
                        :id="'email' + index"
                        v-slot="{ errors }"
                        :vid="'email' + index"
                        name="email"
                        rules="required|email"
                      >
                        <v-text-field
                          v-model="nameAndEmail.email"
                          label="Email*"
                          :error-messages="errors"
                        />
                      </ValidationProvider>
                    </v-col>

                    <v-col cols="2">
                      <v-btn
                        rounded
                        depressed
                        outlined
                        color="grey darken-3"
                        :small="$vuetify.breakpoint.xsOnly"
                        @click="removeNameAndEmail(index)"
                      >
                        <v-icon color="black">{{ icons.mdiClose }}</v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>
                <v-row>
                  <v-btn
                    rounded
                    depressed
                    outlined
                    color="grey darken-3"
                    @click="addNameAndEmailFields()"
                  >
                    Add another email address
                  </v-btn>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-textarea
                      v-model="message"
                      label="Message (optional)"
                      rows="1"
                      auto-grow
                    />
                  </v-col>
                </v-row>

                <v-btn
                  class="submit-button"
                  :loading="buttonLoading"
                  rounded
                  depressed
                  large
                  type="submit"
                  color="primary"
                  :disabled="!nameAndEmails.length"
                >
                  <span>Send email(s)</span
                  ><v-icon small>{{ icons.mdiArrowRight }}</v-icon>
                </v-btn>
              </v-form>
            </ValidationObserver>

            <!-- Share links -->
            <v-row class="mt-8">
              <v-col cols="6">
                <h3>Share your referral link on social media</h3>
              </v-col>
              <v-col cols="3" sm="2" offset-sm="1">
                <a
                  :href="`https://twitter.com/intent/tweet?url=${referralLink}&text=Take a look at ${whitelabel.name}`"
                  target="_blank"
                >
                  <v-img
                    :src="require('@/assets/social/twitter-logo.png')"
                    alt="share on twitter"
                    class="referral-social-link"
                  />
                </a>
              </v-col>
              <v-col cols="3" sm="2">
                <a
                  :href="`https://www.facebook.com/sharer/sharer.php?u=${referralLink}&t=Take a look at ${whitelabel.name}`"
                  target="_blank"
                >
                  <v-img
                    :src="require('@/assets/social/facebook-logo.png')"
                    alt="share on facebook"
                    class="referral-social-link"
                  />
                </a>
              </v-col>
            </v-row>

            <v-row class="mt-5">
              <v-col cols="12" md="9">
                <v-text-field
                  id="referralLink"
                  v-model="referralLink"
                  label="Your unique referral link"
                  readonly
                  outlined
                  :append-outer-icon="icons.mdiContentCopy"
                  @click:append-outer="copyReferralLink"
                />
              </v-col>
            </v-row>

            <v-row class="refer-a-friend-terms-and-conditions">
              <v-col cols="12">
                <p>
                  Receive {{ referralAmount }} when somebody you refer completes
                  one of the following:
                </p>
                <ul>
                  <li>Pays for a will</li>
                  <li>
                    Creates and signs a will with
                    {{ whitelabel.internalExecutorName }} selected as Executor
                  </li>
                  <li>Takes out a QuickWill Policy</li>
                </ul>
              </v-col>
            </v-row>
          </div>
        </v-card-text>

        <v-card-actions class="crud-card-actions">
          <v-row no-gutters>
            <v-col cols="12">
              <v-btn
                class="right-edit-btn"
                rounded
                depressed
                large
                color="primary"
                @click="closeDialog()"
              >
                Close
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ErrorSnackbar v-model="showError" />
    <SuccessSnackbar v-model="showSuccess" :message="successMessage" />
  </div>
</template>

<script>
  import { GENERATE_REFERRAL_LINK_MUTATION } from '../../constants/graphql/users';
  import { REFER_A_FRIEND_QUERY } from '../../constants/graphql/people';
  import { EMAIL_REFER_A_FRIEND_MUTATION } from '../../constants/graphql/referrals';
  import SuccessSnackbar from '../common/SuccessSnackbar.vue';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';

  export default {
    name: 'ReferAFriend',
    components: {
      ErrorSnackbar,
      SuccessSnackbar,
    },
    mixins: [formHelperMixin],
    data() {
      return {
        dialog: false,
        loading: true,
        showError: false,
        showSuccess: false,
        referralLink: null,
        successMessage: null,
        nameAndEmails: [],
        message: null,
        testator: null,
      };
    },
    computed: {
      showReferAFriend() {
        return (
          this.testator &&
          (!this.testator.company || this.testator.company.referAFriend) &&
          this.testator.willCountry
        );
      },
      referralAmount() {
        return this.testator.willCountry == 'ZA' ? 'R300' : '£15';
      },
    },
    watch: {
      dialog(newValue) {
        if (newValue == true) {
          this.referralLink
            ? (this.loading = false)
            : this.findOrCreateReferralLink();
        }
      },
    },
    methods: {
      findOrCreateReferralLink() {
        this.loading = true;
        const userId = this.userId;

        this.$apollo
          .mutate({
            mutation: GENERATE_REFERRAL_LINK_MUTATION,
            variables: {
              userId,
            },
          })
          .then(response => {
            this.loading = false;
            this.referralLink = response.data.generateReferralLink;
          })
          .catch(error => {
            this.closeDialog();
            this.showError = true;
            throw error;
          });
      },
      submit() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.emailReferAFriend();
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      emailReferAFriend() {
        this.buttonLoading = true;
        const userId = this.userId;
        const message = this.message;
        const nameAndEmailInputs = this.nameAndEmails;

        this.$apollo
          .mutate({
            mutation: EMAIL_REFER_A_FRIEND_MUTATION,
            variables: {
              userId,
              nameAndEmailInputs,
              message,
            },
          })
          .then(response => {
            this.buttonLoading = false;
            this.successMessage =
              'Emails with your referral link sent successfully';
            this.showSuccess = true;
            this.message = null;
          })
          .catch(error => {
            this.closeDialog();
            this.showError = true;
            throw error;
          });
      },
      closeDialog() {
        this.dialog = false;
      },
      addNameAndEmailFields() {
        this.nameAndEmails.push({ name: null, email: null });
      },
      removeNameAndEmail(index) {
        this.nameAndEmails.splice(index, 1);
      },
      copyReferralLink() {
        var copyText = document.getElementById('referralLink');
        copyText.removeAttribute('disabled');
        copyText.select();
        copyText.setSelectionRange(0, 99999); /* For mobile devices */

        document.execCommand('copy');

        // Set back to disabled
        copyText.setAttribute('disabled', 'disbaled');

        /* Alert the copied text */
        this.successMessage = 'Referral link copied to clipboard';
        this.showSuccess = true;
      },
    },
    apollo: {
      testator: {
        query: REFER_A_FRIEND_QUERY,
        variables() {
          return {
            id: this.testatorId,
          };
        },
        fetchPolicy: 'cache-only',
        result(response) {
          if (
            response.data &&
            response.data.testator &&
            response.data.testator.people
          ) {
            this.nameAndEmails = [];
            for (const person of response.data.testator.people) {
              if (person.email) {
                this.nameAndEmails.push({
                  name: person.fullName,
                  email: person.email,
                });
              }
            }

            if (this.nameAndEmails.length == 0) {
              this.addNameAndEmailFields();
            }
            this.loading = false;
          }
        },
      },
    },
  };
</script>

<style lang="scss">
  .refer-a-friend {
    .referral-social-link {
      max-width: 60px;
    }

    .open-modal-link {
      color: rgba(0, 0, 0, 0.87);
      display: block;
      text-align: center;
      white-space: initial;
    }

    .v-banner__actions {
      max-width: 50px;
      margin-left: 20px;
    }
  }

  .refer-a-friend-terms-and-conditions {
    font-weight: bold;

    .col {
      padding-top: 0;
    }

    p {
      margin-bottom: 10px;
    }

    li {
      margin-bottom: 4px;
    }
  }
</style>
